import type { ChangeEvent, JSX, Ref } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import { contextFactory } from '~/utils/contextFactory';
import type {
  BaseSelectContextProps,
  BaseSelectProps,
  SelectFilterItem,
  SelectItem,
  SelectItems,
} from './BaseSelect.props';

// This component is a base select component that can be used to create custom select components.
// It shouldn't be used directly, but instead to compose other select components.

// oxlint-disable-next-line typescript/no-explicit-any -- We need to use any to create a generic context
export const [BaseSelectContext, useBaseSelectContext] = contextFactory<BaseSelectContextProps<any>>();

function searchInObject(obj: unknown, term: string): boolean {
  if (typeof obj !== 'object' || obj === null) {
    return String(obj).toLowerCase().includes(term);
  }

  return Object.values(obj).some((value) => {
    if (typeof value === 'object' && value !== null) {
      return searchInObject(value, term);
    }
    return String(value).toLowerCase().includes(term);
  });
}

export function BaseSelectComponent<TItemValue>(
  {
    applyLabel = 'Apply',
    backLabel = 'Back',
    children,
    disableFiltering,
    indeterminateItems,
    items = [],
    loading,
    noDataLabel = 'No data',
    resetLabel = 'Reset',
    searchPlaceholder,
    selectedItems = [],
    selectedLimit,
    width,
    onSelectChange,
    toggleOpen,
  }: BaseSelectProps<TItemValue>,
  forwardedRef: Ref<HTMLDivElement>,
) {
  const [searchTerm, setSearchTerm] = useState('');

  function resetSearchTerm() {
    setSearchTerm('');
  }

  function handleSearchChange(event?: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event ? event.target.value : '');
  }

  const searchTokens = useMemo(
    () =>
      searchTerm
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0),
    [searchTerm],
  );

  const filteredItems = useMemo(() => {
    if (disableFiltering || searchTokens.length === 0) {
      return items;
    }

    const matchesSearch = (item: SelectItem<TItemValue> | SelectFilterItem<TItemValue>) =>
      searchTokens.some((term) => item.label.toLowerCase().includes(term) || searchInObject(item.value, term));

    return items.flatMap((item: SelectItems<TItemValue>): Array<SelectItems<TItemValue>> => {
      // Deliberately ignores the group's own label and value: the value is an internal key that matched unrelated searches.
      if ('filters' in item) {
        const filters = item.filters.filter(matchesSearch);
        return filters.length > 0 ? [{ ...item, filters }] : [];
      }

      return matchesSearch(item) ? [item] : [];
    });
  }, [items, disableFiltering, searchTokens]);

  const contextValue: BaseSelectContextProps<TItemValue> = {
    applyLabel,
    backLabel,
    handleSearchChange,
    handleSelectChange: onSelectChange,
    indeterminateItems,
    items: filteredItems,
    loading,
    noDataLabel,
    resetLabel,
    resetSearchTerm,
    searchPlaceholder,
    searchTerm,
    selectedItems,
    selectedLimit,
    toggleOpen,
    width,
  };

  return (
    <div ref={forwardedRef}>
      <BaseSelectContext.Provider value={contextValue}>{children}</BaseSelectContext.Provider>
    </div>
  );
}

export const BaseSelect = forwardRef(BaseSelectComponent) as <TItemValue>(
  props: BaseSelectProps<TItemValue> & { ref?: Ref<HTMLDivElement> },
) => JSX.Element;
